import React, { useEffect, useState } from 'react'

import { SxProps } from '@mui/material'

import { useReportEdition } from 'redux/hooks/Reports'
import { useReportEditionActions } from 'redux/actions/ReportEditions'

import { EDataMode, EPublicationStatus } from 'types/Enums'
import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'

import { useTranslation } from 'language/translator'
import { useParams, useQuery } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import Divider from 'components_new/atoms/Divider'
import Skeleton from 'components_new/atoms/Skeleton'
import StatusBox from 'components_new/molecules/StatusBox'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

import EditableTitle from 'components_new/molecules/EditableTitle'
import SidebarDialog from 'components_new/molecules/SidebarDialog'
import StepperWizard from 'components_new/molecules/StepperWizard'

import DataStatus from '../DataStatus'
import PeriodSelector from '../DashboardUserFilter/PeriodSelector'
import ReportFilters from '../ReportFilters'

import { getPeriodFilterDisplayValue } from '../DashboardUserFilter/utils'
import { getDatePeriodFromPeriodFilter } from 'utils/periodFilter'
import { convertFilterValuesToAppliedFilters } from './utils'

interface ReportEditionSettingsProps {
  activeFilters: Record<string, string[]>
  editMode: boolean
  periodFilter: PeriodFilter | CustomPeriodFilter
  setActiveFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  setPeriodFilter: React.Dispatch<
    React.SetStateAction<PeriodFilter | CustomPeriodFilter>
  >
  sx?: SxProps
}

const ReportEditionSettings = (props: ReportEditionSettingsProps) => {
  const {
    activeFilters,
    editMode,
    periodFilter,
    setActiveFilters,
    setEditMode,
    setPeriodFilter,
    sx
  } = props

  const { edition_id, id } = useParams<{ edition_id: string; id: string }>()
  const edition = useReportEdition(edition_id)
  const { query } = useQuery()
  const { tryUpdateReportEdition } = useReportEditionActions()

  const { t, translateEnum } = useTranslation()

  const [wizardStep, setWizardStep] = useState<number>(0)
  const [title, setTitle] = useState<string>(edition?.title ?? '')

  const [lockOpen, setLockOpen] = useState<boolean>(false)
  const [publishOpen, setPublishOpen] = useState<boolean>(false)
  const [buttonLoading, setButtonLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!edition) return
    if (edition.status === EPublicationStatus.PUBLISHED) {
      setWizardStep(3)
    } else if (edition.locked_at !== null) {
      setWizardStep(1)
    }
  }, [!!edition])

  // loading
  if (!edition)
    return (
      <Box sx={sx}>
        <Skeleton height={36} variant="rounded" />
        <Skeleton height={32} variant="rounded" />

        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={96} variant="rounded" />
        ))}
      </Box>
    )

  // success
  return (
    <>
      <Box sx={sx}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 4
          }}
        >
          {[
            {
              title: t('global', 'edition'),
              content: (
                <EditableTitle
                  actions={true}
                  autoFocus={Boolean(query['edit'])}
                  disabled={!editMode}
                  gutterBottom={true}
                  onSubmit={(value) =>
                    tryUpdateReportEdition(edition_id, {
                      title: value ?? undefined
                    })
                  }
                  placeholder={t('global', 'untitled')}
                  value={edition.title}
                  variant="subtitle2"
                />
              )
            },
            {
              title: t('global', 'status'),
              content: (
                <Chip
                  color={
                    edition.status === EPublicationStatus.PUBLISHED
                      ? undefined
                      : 'primary'
                  }
                  label={translateEnum('report_status', edition.status)}
                  size="small"
                />
              )
            }
          ].map((item, i) => (
            <Box key={i}>
              <Text color="text.secondary" variant="body2">
                {item.title}
              </Text>
              {item.content}
            </Box>
          ))}

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Divider flexItem={true} orientation="vertical" />

            <DataStatus
              date={edition.locked_at}
              mode={edition.locked_at ? EDataMode.LOCKED : EDataMode.SYNCED}
            />

            {edition.locked_at ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                <Chip
                  color="primary"
                  label={getPeriodFilterDisplayValue(
                    { from: edition.from_date, to: edition.to_date },
                    t
                  )}
                  size="small"
                />
                {(edition.applied_filters || []).map((item) =>
                  item.values.map((value) => (
                    <Chip
                      color="primary"
                      key={`${item.attribute_id}${value}`}
                      label={value}
                      size="small"
                    />
                  ))
                )}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Collapse in={editMode}>
          <StepperWizard
            active={wizardStep}
            setActive={setWizardStep}
            steps={[
              {
                action: {
                  title: t('global', 'lock'),
                  onClick: () => {
                    setLockOpen(true)

                    return false
                  }
                },
                content: (
                  <>
                    <PeriodSelector
                      allowReset={false}
                      onSubmit={setPeriodFilter}
                      periodFilter={periodFilter}
                    />

                    <ReportFilters
                      activeFilters={activeFilters}
                      id={id}
                      setActiveFilters={setActiveFilters}
                    />
                  </>
                ),
                completedIcon: { name: 'Lock' },
                completedTitle: t('report_edition', 'data_locked'),
                description: t('report_edition', 'set_period_and_filters'),
                focused: true,
                title: t('report_edition', 'lock_data')
              },
              {
                action: {
                  title: t('global', 'next'),
                  onClick: () => {
                    setTitle(edition?.title || '')

                    return true
                  }
                },
                completedIcon: { name: 'Edit' },
                completedTitle: t('report_edition', 'content_edited'),
                content: (
                  <StatusBox
                    allowClose={false}
                    icon={{ name: 'Edit' }}
                    severity="info"
                    title={t('report_edition', 'you_can_now_edit')}
                  >
                    {t('report_edition', 'set_numbers_and_content')}
                  </StatusBox>
                ),
                title: t('report_edition', 'edit_content')
              },
              {
                action: {
                  endIcon: { name: 'Published' },
                  title: t('global', 'publish'),
                  onClick: () => {
                    setPublishOpen(true)

                    return false
                  }
                },
                completedTitle: t('global', 'published'),
                content: (
                  <>
                    <TextField
                      autoFocus={true}
                      label={t('global', 'edition')}
                      onChange={(ev) => setTitle(ev.target.value)}
                      value={title}
                      sx={{ maxWidth: 'title' }}
                    />
                  </>
                ),
                description: t('report_edition', 'title_helper_text'), // $TODO
                title: t('global', 'publish')
              }
            ]}
            sx={{ mb: 4 }}
          />
        </Collapse>

        <Collapse in={!editMode && edition.locked_at === null}>
          <PeriodSelector
            allowReset={false}
            onSubmit={setPeriodFilter}
            periodFilter={periodFilter}
            sx={{ mb: 2 }}
          />
          <ReportFilters
            activeFilters={activeFilters}
            id={id}
            setActiveFilters={setActiveFilters}
            sx={{ mb: 4 }}
          />
        </Collapse>
      </Box>

      {/*-- dialogs --*/}

      {/*-- publish --*/}
      <SidebarDialog
        actions={
          <>
            <Button onClick={() => setPublishOpen(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              loading={buttonLoading}
              loadingTitle={t('report_edition', 'publishing')}
              onClick={() => {
                setButtonLoading(true)

                tryUpdateReportEdition(
                  edition_id,
                  {
                    status: EPublicationStatus.PUBLISHED,
                    title: title
                  },
                  (err) => {
                    if (!err) {
                      setPublishOpen(false)
                      setWizardStep((s) => s + 1)
                      setEditMode(false)
                    }

                    setButtonLoading(false)
                  }
                )
              }}
            >
              {t('global', 'publish')}
            </Button>
          </>
        }
        maxWidth="xs"
        onClose={() => setPublishOpen(false)}
        open={publishOpen}
        title={`${t('global', 'publish')} ${t('global', 'edition', true)}?`}
      >
        {t('report_edition', 'publish_dialog_body')}
      </SidebarDialog>

      {/*-- lock --*/}
      <SidebarDialog
        actions={
          <>
            <Button
              disabled={buttonLoading}
              onClick={() => setLockOpen(false)}
              variant="text"
            >
              {t('global', 'cancel')}
            </Button>
            <Button
              loading={buttonLoading}
              loadingTitle={t('report_edition', 'locking_data')}
              onClick={() => {
                if (!periodFilter) return

                const period = getDatePeriodFromPeriodFilter(periodFilter)

                setButtonLoading(true)

                tryUpdateReportEdition(
                  edition_id,
                  {
                    lock: true,
                    from_date: period.from,
                    to_date: period.to,
                    applied_filters:
                      convertFilterValuesToAppliedFilters(activeFilters)
                  },
                  (err) => {
                    if (!err) {
                      setLockOpen(false)
                      setWizardStep((s) => s + 1)
                    }

                    setButtonLoading(false)
                  }
                )
              }}
            >
              {t('report_edition', 'lock_data')}
            </Button>
          </>
        }
        maxWidth="xs"
        onClose={() => {
          if (!buttonLoading) {
            setLockOpen(false)
          }
        }}
        open={lockOpen}
        title={`${t('report_edition', 'lock_data')}?`}
      >
        <Text color="text.primary" sx={{ mb: 2 }}>
          {t('report_edition', 'lock_dialog_body')}
        </Text>
      </SidebarDialog>
    </>
  )
}

export default ReportEditionSettings
